import React, { FC, ReactNode, useState } from 'react';

import styled from 'styled-components';
import { addMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import Datepicker from 'components/form/Datepicker';
import MultiSelect from 'components/table/MultiSelectFilter';
import SingleSelect from 'components/form/SingleSelect';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { FaFilter, FaSyncAlt, FaExclamationTriangle, FaExclamationCircle } from 'react-icons/fa';
import { ITableFilter } from './types';

interface IProps {
  filters?: ITableFilter[];
  showFilters: boolean;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  hideSearch: boolean;
  showFilterWarning?: boolean;
  hasActiveFilters?: boolean;
  unprocessedLeaves?: {
    count: number;
    min_date: string | null;
    max_date: string | null;
  } | null;
}

interface TablesWrapperProps {
  showFilters: boolean;
}

type DateType = Date | undefined;

const TableFilters: FC<IProps> = ({
  filters,
  showFilters,
  setShowFilters,
  hideSearch,
  showFilterWarning,
  hasActiveFilters,
  unprocessedLeaves,
}: IProps) => {
  const [startDate, setStartDate] = useState<DateType>();
  const [allDatePickerDates, setAllDatePickerDates] = useState<{ index: number; date: DateType }[]>([]);
  const [monthYearDate, setMonthYearDate] = useState<DateType>();
  const [monthYearStartDate, setMonthYearStartDate] = useState<DateType>();
  const [monthYearEndDate, setMonthYearEndDate] = useState<DateType>();

  const location = useLocation();
  const { pathname } = location;
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const toggleFilters = () => {
    setShowFilters(prev => !prev);
  };

  const handleMonthYearEndDateChange = (date: DateType, filterSubType: string) => {
    if (!date && monthYearStartDate) {
      let adjustment = 2;
      if (filterSubType === 'leavesCompleted') {
        adjustment = 0;
      } else if (filterSubType === 'leavesWaitingApproval') {
        adjustment = 1;
      }
      const newEndDate = addMonths(monthYearStartDate, adjustment);
      setMonthYearEndDate(newEndDate);
    }
    if (date) {
      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      setMonthYearEndDate(lastDay);
    }
  };

  const setDateForIndex = (index: number, date: DateType) => {
    // Check if date for index already exists
    const existingDate = allDatePickerDates.find(item => item.index === index);

    if (existingDate) {
      // Update the date for this index
      const updatedDates = allDatePickerDates.map(item => (item.index === index ? { index, date } : item));
      setAllDatePickerDates(updatedDates);
    } else {
      // Add new date for this index
      setAllDatePickerDates([...allDatePickerDates, { index, date }]);
    }
  };

  const getDateForIndex = (index: number) => {
    const dateObject = allDatePickerDates.find(item => item.index === index);
    return dateObject ? dateObject.date : undefined;
  };

  const checkIfJanuary = (d: DateType) => {
    if (d?.getMonth() === 0) {
      const previousYear = d.getFullYear() - 1;
      const previousYearInDecember = new Date(previousYear, 11, 31);

      return previousYearInDecember;
    }
    return d;
  };

  const refreshDocuments = () => {
    let formattedPathname = pathname.slice(1).replace(/\//g, '-');

    if (pathname.includes('/documents/ready-for-sign-and-send')) {
      formattedPathname = 'documents-for-sign';
    } else if (pathname.includes('/leaves')) {
      formattedPathname = 'leaves';
    } else if (pathname.includes('/my-documents/ready-for-sign-send')) {
      formattedPathname = 'my-documents-for-sign';
    }

    queryClient.resetQueries(formattedPathname);
  };

  const shouldShowFilterButton = (filters && filters.length > 0) || !hideSearch;

  return (
    <div className="d-flex flex-column align-items-end">
      {shouldShowFilterButton && (
        <div className="mt-2 d-flex">
          {showFilterWarning && hasActiveFilters && (
            <MobileWarningIconWrapper className="d-md-none">
              <TooltipWrapper
                id="filter-warning-tooltip-mobile"
                overlay={t('table.filterWarning')}
              >
                <FaExclamationTriangle style={{ color: '#ffc107', fontSize: '20px' }} />
              </TooltipWrapper>
            </MobileWarningIconWrapper>
          )}
          {unprocessedLeaves && unprocessedLeaves.count > 0 && (
            <MobileWarningIconWrapper className="d-md-none">
              <TooltipWrapper
                id="tooltip-unprocessed-leaves-mobile"
                overlay={t('leaves.employer.unprocessedLeavesWarning', {
                  minDate: unprocessedLeaves.min_date
                    ? new Date(unprocessedLeaves.min_date).toLocaleDateString('bg-BG')
                    : '',
                  maxDate: unprocessedLeaves.max_date
                    ? new Date(unprocessedLeaves.max_date).toLocaleDateString('bg-BG')
                    : '',
                })}
              >
                <FaExclamationCircle style={{ color: '#ffc107', fontSize: '20px' }} />
              </TooltipWrapper>
            </MobileWarningIconWrapper>
          )}
          {(pathname.includes('/documents/') ||
            pathname.includes('/leaves') ||
            pathname.includes('/my-documents/')) && (
            <RefreshButton
              className="refresh-button"
              onClick={refreshDocuments}
            >
              <FaSyncAlt style={{ fontSize: '16px' }} />
            </RefreshButton>
          )}
          <FilterButton>
            <TooltipWrapper
              id="tooltip-add-btn"
              overlay={t('documents.table.filtersBtn')}
              wrapperClass="d-block col-xs-12 col-md-auto mb-0"
            >
              <Button
                ariaLabel="Filter button"
                onClick={toggleFilters}
                className="btn btn-secondary btn-sm"
              >
                <FaFilter />
              </Button>
            </TooltipWrapper>
          </FilterButton>
        </div>
      )}
      {filters && filters.length > 0 && (
        <TablesWrapper showFilters={showFilters}>
          {unprocessedLeaves && unprocessedLeaves.count > 0 && (
            <div
              className="d-none d-md-flex align-items-center justify-content-center mr-3"
              style={{ height: '100%' }}
            >
              <TooltipWrapper
                id="tooltip-unprocessed-leaves"
                overlay={t('leaves.employer.unprocessedLeavesWarning', {
                  minDate: unprocessedLeaves.min_date
                    ? new Date(unprocessedLeaves.min_date).toLocaleDateString('bg-BG')
                    : '',
                  maxDate: unprocessedLeaves.max_date
                    ? new Date(unprocessedLeaves.max_date).toLocaleDateString('bg-BG')
                    : '',
                })}
              >
                <FaExclamationCircle style={{ color: '#ffc107', fontSize: '22px', cursor: 'pointer' }} />
              </TooltipWrapper>
            </div>
          )}
          {
            filters.map((filter, index) => {
              const {
                type,
                subType,
                placeholder,
                options,
                isMulti,
                showTotal,
                onChange,
                name,
                value,
                limitOptions,
                allowSelectAll,
                ariaLabel,
                size,
              } = filter;
              const className = `c-filter ${index === 0 ? '' : 'ml-md-3'} ml-0 ${
                index === filters.length - 1 ? '' : 'mr-3'
              } ${name === 'category_id' ? 'c-filter--200' : ''} mr-md-0`;

              // Monthpicker filter
              if (type === 'monthyearpicker') {
                if (!monthYearDate) {
                  const currentDay = new Date();
                  setMonthYearDate(currentDay);
                }
                return (
                  <Datepicker
                    key={`monthyearpicker-${index}`}
                    ariaLabel={ariaLabel}
                    wrapperClassName={`c-filter--date-width c-filter--small ${className}`}
                    className="form-control"
                    placeholder={placeholder}
                    onChange={(date: any) => {
                      const selectedDate = date || new Date();
                      setMonthYearDate(selectedDate);
                      onChange(selectedDate);
                    }}
                    selected={monthYearDate}
                    dateFormat="MM/yyyy"
                    showMonthYearPicker
                    isClearable
                  />
                );
              }
              // Monthyearpicker filter
              if (type === 'monthyearpickerstart') {
                if (!monthYearStartDate) {
                  const today = new Date();
                  const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                  setMonthYearStartDate(firstDayOfCurrentMonth);
                }
                return (
                  <Datepicker
                    key={`monthyearpickerstart-${index}`}
                    ariaLabel={ariaLabel}
                    wrapperClassName={`c-filter--date-width c-filter--small ${className}`}
                    className="form-control"
                    placeholder={placeholder}
                    onChange={(date: Date) => {
                      const selectedDate = date || new Date();
                      setMonthYearStartDate(selectedDate);
                      onChange(selectedDate);
                      // Start Day can not be after monthYearEndDate
                    }}
                    selected={monthYearStartDate}
                    startDate={monthYearStartDate}
                    selectsStart
                    dateFormat="MM/yyyy"
                    showMonthYearPicker
                  />
                );
              }
              // Monthyearpickerend filter
              if (type === 'monthyearpickerend') {
                if (!monthYearEndDate && monthYearStartDate) {
                  if (['leavesCompleted', 'leavesWaitingApproval', 'leaveNextMonth'].includes(subType)) {
                    const newDate = new Date();

                    if (newDate.getMonth() === 11) {
                      newDate.setFullYear(newDate.getFullYear() + 1);
                      newDate.setMonth(0);
                    } else {
                      newDate.setMonth(11);
                    }

                    newDate.setDate(31);
                    return setMonthYearEndDate(newDate);
                  }
                  if (subType === 'leavesWaitingApproval') {
                    return setMonthYearEndDate(addMonths(monthYearStartDate, 1));
                  }
                  setMonthYearEndDate(addMonths(monthYearStartDate, 2));
                }
                return (
                  <Datepicker
                    key={`monthyearpickerend-${index}`}
                    ariaLabel={ariaLabel}
                    wrapperClassName={`c-filter--date-width c-filter--small ${className}`}
                    className="form-control danger"
                    placeholder={placeholder}
                    onChange={(date: Date) => {
                      handleMonthYearEndDateChange(date, subType);
                      const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                      onChange(lastDayOfMonth);
                    }}
                    selected={monthYearEndDate}
                    startDate={monthYearStartDate}
                    selectsEnd
                    dateFormat="MM/yyyy"
                    showMonthYearPicker
                    // minDate={addMonths(monthYearStartDate + 1, 1)}
                    minDate={checkIfJanuary(monthYearStartDate)}
                  />
                );
              }
              // Datepicker filter
              if (type === 'datepicker') {
                return (
                  <Datepicker
                    key={`datepicker-${index}`}
                    ariaLabel={ariaLabel}
                    wrapperClassName={`c-filter--date-width c-filter--small ${className}`}
                    className="form-control"
                    placeholder={placeholder}
                    onChange={(date: Date) => {
                      setStartDate(date);
                      onChange(date);
                    }}
                    selected={startDate}
                    dateFormat="dd.MM.yyyy"
                    maxDate={new Date()}
                    isClearable
                  />
                );
              }
              if (type === 'alldatepicker') {
                return (
                  <Datepicker
                    key={`alldatepicker-${index}`}
                    ariaLabel={ariaLabel}
                    wrapperClassName={`c-filter--max-width-160 ${className}`}
                    className="form-control"
                    placeholder={placeholder}
                    onChange={(date: Date) => {
                      setDateForIndex(index, date);
                      onChange(date);
                    }}
                    selected={getDateForIndex(index)}
                    dateFormat="dd.MM.yyyy"
                    isClearable
                  />
                );
              }
              if (type === 'icon') {
                return (
                  <div
                    key={`icon-${index}`}
                    aria-label={ariaLabel}
                    className={`d-flex align-items-center ${className}`}
                    style={{
                      fontSize: '22px',
                      minWidth: '0px',
                      cursor: 'pointer',
                    }}
                  >
                    {filter.icon}
                  </div>
                );
              }

              // Multi select filter
              if (isMulti) {
                return (
                  <MultiSelect
                    key={`multiselect-${index}`}
                    ariaLabel={ariaLabel}
                    // @ts-ignore
                    value={options && options?.filter(option => value?.includes(option.value.toString()))}
                    placeholder={placeholder}
                    options={options}
                    onChange={onChange}
                    className={`${className} ${size === 'large' ? 'c-filter--large' : ''}`}
                    isClearable
                    showTotal={showTotal}
                    limitOptions={limitOptions}
                    allowSelectAll={allowSelectAll}
                  />
                );
              }

              // Single select filter
              return (
                <SingleSelect
                  key={`singleselect-${index}`}
                  ariaLabel={ariaLabel}
                  value={options?.find(option => option.value === value)}
                  placeholder={placeholder}
                  options={options}
                  onChange={onChange}
                  className={`${className} ${size === 'large' ? 'c-filter--large' : ''}`}
                  isClearable
                />
              );
            }) as ReactNode
          }
        </TablesWrapper>
      )}
    </div>
  );
};

const TablesWrapper = styled.div<TablesWrapperProps>`
  display: flex;
  justify-content: flex-end;
  flex-grow: 1;
  flex-wrap: wrap;
  align-items: center;
  max-width: max-content;
  gap: 1rem 0;

  @media (max-width: 991px) {
    flex-wrap: wrap;
  }

  @media (max-width: 768px) {
    justify-content: center;
    gap: 1rem;
    margin-bottom: 1rem;

    // Control visibility based on props
    display: ${props => (props.showFilters ? 'flex' : 'none')};

    > * {
      width: 100%;
      margin-right: 0 !important;
    }
  }
`;

const FilterButton = styled.div`
  display: none;
  @media (max-width: 768px) {
    height: 30px;
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
  }
`;

const RefreshButton = styled.div`
  display: none;

  &:hover {
    color: #32bfa3;
  }

  @media (max-width: 768px) {
    height: 30px;
    width: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    margin-right: 0.5rem;
    margin-top: 4px;
  }
`;

const MobileWarningIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.5rem;
  height: 30px;
  margin-top: auto;
  margin-bottom: auto;
`;

export default TableFilters;
